import { Stack } from "@altimateai/lego";
import { Button, Card, CardContent, Typography } from "@altimateai/lego";
import { IconBolt, IconCircleCheck, IconFileText } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { dropdownMenuData, PathOption, pathConfigs } from "../../constants";
import Dropdown from "../dropdown/Dropdown";

interface PlaygroundSectionProps {
  launchDemo: () => void;
  isDemoLoading: boolean;
}

const playgroundConfig = pathConfigs[PathOption.Playground];
const playgroundDropdowns = dropdownMenuData[PathOption.Playground];

function PlaygroundSection({ launchDemo, isDemoLoading }: PlaygroundSectionProps) {
  return (
    <Stack direction="column" gap="md">
      <Card className="al-rounded-xl al-py-6 sm:al-py-8 al-px-4 !al-bg-gradient-to-r al-from-purple-50 al-to-blue-50 al-border-2 al-border-purple-200">
        <CardContent>
          <Stack
            direction="column"
            className="sm:al-flex-row sm:al-items-center sm:al-justify-between al-gap-4 sm:al-gap-6"
          >
            <Stack direction="column" className="al-flex-1">
              <Stack direction="row" align="center" gap="xs" className="al-mb-3">
                <IconBolt size={24} className="al-text-purple-600 al-shrink-0" />
                <Typography variant="h4" weight="semibold" className="al-text-gray-900">
                  Want to try it out first?
                </Typography>
              </Stack>
              <Typography size="sm" className="al-text-gray-700 al-mb-2">
                Launch a fully functional demo studio to explore AI capabilities
              </Typography>
              <Stack direction="row" align="center" gap="xs" className="al-flex-wrap">
                <IconCircleCheck size={16} className="al-text-green-600 al-shrink-0" />
                <Typography size="xs" className="al-text-gray-600">
                  No setup required • Sample data included • Full features available
                </Typography>
              </Stack>
            </Stack>
            <Button
              onClick={() => launchDemo()}
              disabled={isDemoLoading}
              size="lg"
              className="al-bg-gradient-to-r al-from-purple-600 al-to-blue-600 hover:al-from-purple-700 hover:al-to-blue-700 al-shadow-lg hover:al-shadow-xl al-transition-all al-w-full sm:al-w-auto al-shrink-0"
            >
              {isDemoLoading ? "Launching..." : "Try Now"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card className="al-rounded-xl al-py-8 al-px-4 !al-bg-blue-50">
        <CardContent>
          <Stack direction="column" gap="xl">
            <Stack direction="column">
              <Typography variant="h4" weight="semibold" className="al-text-gray-900">
                Get Started with Studio
              </Typography>
              <Typography size="sm" className="al-text-gray-600">
                Follow these steps to set up and start using the AI-powered Studio
              </Typography>
            </Stack>
            <Stack gap="sm" direction="column" className="al-py-2 sm:al-flex-row">
              <Button
                size="lg"
                className="al-bg-slate-900 hover:al-bg-slate-800 al-py-7 al-px-8 al-text-base al-w-full sm:al-w-auto"
                asChild
              >
                <Link
                  to="https://docs.myaltimate.com/studio/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconFileText size={18} />
                  View Documentation
                </Link>
              </Button>
            </Stack>
            <Stack direction="column" gap="md" className="al-space-y-2">
              {playgroundDropdowns.map((item, index) => (
                <Dropdown
                  key={item.title}
                  stepNumber={String(index + 1)}
                  title={item.title}
                  subtitle={item.subtitle}
                  tutorial={item.tutorial}
                  borderColor={playgroundConfig.borderColor}
                  bgColor={playgroundConfig.bgColor}
                />
              ))}
            </Stack>
            <Stack
              direction="column"
              align="center"
              justify="between"
              className="al-w-full al-pt-6 al-border-t al-border-gray-200 al-p-4 al-rounded-lg al-gap-4 sm:al-flex-row sm:al-items-center"
            >
              <Stack direction="column" gap="xs" className="al-text-center sm:al-text-left">
                <Typography variant="section-heading" className="al-text-gray-900">
                  Ready to get started?
                </Typography>
                <Typography size="sm" className="al-text-gray-600">
                  Launch Studio and start building with AI assistance
                </Typography>
              </Stack>
              <Button asChild size="lg" className="al-w-full sm:al-w-auto al-shrink-0">
                <Link to="/studio">Go to Studio</Link>
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

export { PlaygroundSection };
